import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { 
  prepareUsersForExport, 
  exportUsersToCSV, 
  exportUsersToXLS, 
  exportUsersToPDF,
  type ExportUser 
} from '@/utils/exportUsers';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ExportUsersButtonsProps {
  users: any[];
  getSubscriptionForUser: (userId: string) => any;
  getUserSessionInfo: (userId: string) => any;
}

const ExportUsersButtons: React.FC<ExportUsersButtonsProps> = ({
  users,
  getSubscriptionForUser,
  getUserSessionInfo
}) => {
  const [isExporting, setIsExporting] = useState<'csv' | 'xls' | 'pdf' | null>(null);

  const handleExport = async (format: 'csv' | 'xls' | 'pdf') => {
    if (users.length === 0) {
      toast.error('Nenhum usuário para exportar');
      return;
    }

    setIsExporting(format);
    
    try {
      const exportData = prepareUsersForExport(
        users,
        getSubscriptionForUser,
        getUserSessionInfo
      );

      switch (format) {
        case 'csv':
          exportUsersToCSV(exportData);
          toast.success(`${users.length} usuários exportados para CSV`);
          break;
        case 'xls':
          await exportUsersToXLS(exportData);
          toast.success(`${users.length} usuários exportados para Excel`);
          break;
        case 'pdf':
          await exportUsersToPDF(exportData);
          toast.success(`${users.length} usuários exportados para PDF`);
          break;
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar usuários');
    } finally {
      setIsExporting(null);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={isExporting !== null}
              className="h-8 gap-1.5"
            >
              {isExporting === 'csv' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">CSV</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Exportar para CSV (planilha)</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('xls')}
              disabled={isExporting !== null}
              className="h-8 gap-1.5 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300 hover:bg-green-50"
            >
              {isExporting === 'xls' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Excel</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Exportar para Excel (.xlsx)</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={isExporting !== null}
              className="h-8 gap-1.5 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50"
            >
              {isExporting === 'pdf' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">PDF</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Exportar para PDF</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default ExportUsersButtons;
